import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { RoundedRectButton } from "../common_ui_elements/buttons/rounded_rect_button.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { UITextInput, UITextInputStyle } from "../common_ui_elements/ui_text_input.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { FeedbackApiController } from "./feedback_api_controller.js";
import { FeedbackNotifDialog } from "./feedback_notif_dialog.js";

export class FeedbackDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 850;
  private static readonly BG_Y = 100;
  private static readonly BG_H = 880;

  private bg_y: number;
  private name_input!: UITextInput;
  private email_input!: UITextInput;
  private message_input!: UITextInput;
  private send_button!: RoundedRectButton;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( FeedbackDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( FeedbackDialog.BG_Y + FeedbackDialog.BG_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.bg_y = LayoutScaler.yFromDesign( FeedbackDialog.BG_Y );

    this.createBackground();
    this.createTitle();
    this.createInputs();
    this.createSendButton();
    this.createCloseIcon();
  }

  private createBackground(): void
  {
    const bg = new RoundedRectangle(
      this.ctx,
      0,
      this.bg_y,
      this.rect.getW(),
      LayoutScaler.heightFromDesign( FeedbackDialog.BG_H ),
      LayoutScaler.cornerRadiusFromDesign( 20 ),
      ColorScheme.white,
    );
    this.addChild( bg );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( 70 );
    const title_y = this.bg_y + LayoutScaler.yFromDesign( 30 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "FEEDBACK_SCREEN.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.settings_dialog_title_outline,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 800,
        auto_wrap: false,
      }
    );
    this.addChild( title );
  }

  private inputStyle( placeholder_color: string ): UITextInputStyle
  {
    return {
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Left,
      placeholder_color,
      background_color: "#F4F6FA",
      border_color_focused: "#4A90D9",
      text_color: "#26364D",
    };
  }

  private createInputs(): void
  {
    const input_w = this.rect.getW() - LayoutScaler.widthFromDesign( 100 );
    const input_x = LayoutScaler.widthFromDesign( 50 );

    this.name_input = new UITextInput(
      this.ctx,
      input_x,
      this.bg_y + LayoutScaler.yFromDesign( 130 ),
      input_w,
      LayoutScaler.heightFromDesign( 90 ),
      {
        localization_key: "FEEDBACK_SCREEN.YOUR_NAME",
        max_length: 25,
        style: this.inputStyle( "#7C8AA0" ),
      }
    );
    this.name_input.onEscapePressed = () => this.name_input.blur();
    this.name_input.onValueChanged = () =>
    {
      this.name_input.clearError();
      if ( this.name_input.getValue().length >= 25 )
        this.name_input.setError( "FEEDBACK_SCREEN.VALIDATION.NAME.OVERLENGTH" );
      this.updateSendButtonState();
    };
    this.name_input.setOnClick( () =>
    {
      this.blurOtherInputs( this.name_input );
      if ( !this.name_input.is_focused )
        this.name_input.focus();
    } );
    this.addChild( this.name_input );

    this.email_input = new UITextInput(
      this.ctx,
      input_x,
      this.bg_y + LayoutScaler.yFromDesign( 290 ),
      input_w,
      LayoutScaler.heightFromDesign( 90 ),
      {
        localization_key: "FEEDBACK_SCREEN.EMAIL",
        style: this.inputStyle( "#7C8AA0" ),
      }
    );
    this.email_input.onEscapePressed = () => this.email_input.blur();
    this.email_input.onValuePasted = () =>
    {
      this.email_input.clearError();
      this.updateSendButtonState();
    };
    this.email_input.onValueChanged = () =>
    {
      this.email_input.clearError();
      this.updateSendButtonState();
    };
    this.email_input.setOnClick( () =>
    {
      this.blurOtherInputs( this.email_input );
      if ( !this.email_input.is_focused )
        this.email_input.focus();
    } );
    this.addChild( this.email_input );

    this.message_input = new UITextInput(
      this.ctx,
      input_x,
      this.bg_y + LayoutScaler.yFromDesign( 450 ),
      input_w,
      LayoutScaler.heightFromDesign( 220 ),
      {
        localization_key: "FEEDBACK_SCREEN.MESSAGE",
        is_multiline: true,
        style: this.inputStyle( "#7C8AA0" ),
      }
    );
    this.message_input.onEnterPressed = () => void this.submitFeedback();
    this.message_input.onEscapePressed = () => this.message_input.blur();
    this.message_input.onValueChanged = () =>
    {
      this.message_input.clearError();
      this.updateSendButtonState();
    };
    this.message_input.setOnClick( () =>
    {
      this.blurOtherInputs( this.message_input );
      if ( !this.message_input.is_focused )
        this.message_input.focus();
    } );
    this.addChild( this.message_input );
  }

  private createSendButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 400 );
    const btn_h = LayoutScaler.heightFromDesign( 100 );
    const btn_x = ( this.rect.getW() - btn_w ) / 2;
    const btn_y = this.bg_y + LayoutScaler.yFromDesign( 730 );

    this.send_button = new RoundedRectButton(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      "#C7C8C9",
      "",
      "#ffffff",
      btn_h / 2
    );

    const text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "SEND",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 700,
      }
    );
    this.send_button.addChild( text, true );

    this.send_button.setOnClick( () => void this.submitFeedback() );
    this.addChild( this.send_button );

    this.updateSendButtonState();
  }

  private createCloseIcon(): void
  {
    const icon_w = LayoutScaler.widthFromDesign( 81 );
    const icon_h = LayoutScaler.heightFromDesign( 81 );
    const icon_x = this.rect.getW() - icon_w + LayoutScaler.xFromDesign( 40 );
    const icon_y = this.bg_y - icon_h + LayoutScaler.yFromDesign( 30 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );

    icon.setOnClick( () => this.close() );
    this.addChild( icon );
  }

  private blurOtherInputs( target: UITextInput ): void
  {
    if ( this.name_input && this.name_input !== target ) this.name_input.blur();
    if ( this.email_input && this.email_input !== target ) this.email_input.blur();
    if ( this.message_input && this.message_input !== target ) this.message_input.blur();
  }

  private blurAllInputs(): void
  {
    this.name_input?.blur();
    this.email_input?.blur();
    this.message_input?.blur();
  }

  private isAllFieldsValid(): boolean
  {
    const name = this.name_input.getValue().trim();
    const email = this.email_input.getValue().trim();
    const message = this.message_input.getValue().trim();

    return name.length > 0 && name.length <= 25 &&
           email.length > 0 && UITextInput.validateEmail( email ) &&
           message.length > 0;
  }

  private updateSendButtonState(): void
  {
    if ( this.send_button )
    {
      const is_valid = this.isAllFieldsValid();
      this.send_button.setBkColor( is_valid ? "#007CDB" : "#C7C8C9" );
    }
  }

  private async submitFeedback(): Promise<void>
  {
    const name = this.name_input.getValue().trim();
    const email = this.email_input.getValue().trim();
    const message = this.message_input.getValue().trim();

    this.name_input.clearError();
    this.email_input.clearError();
    this.message_input.clearError();

    let has_validation_errors = false;

    if ( name.length === 0 )
    {
      this.name_input.setError( "FEEDBACK_SCREEN.VALIDATION.NAME.EMPTY" );
      has_validation_errors = true;
    }
    else if ( name.length > 25 )
    {
      this.name_input.setError( "FEEDBACK_SCREEN.VALIDATION.NAME.OVERLENGTH" );
      has_validation_errors = true;
    }

    if ( email.length === 0 )
    {
      this.email_input.setError( "FEEDBACK_SCREEN.VALIDATION.EMAIL.EMPTY" );
      has_validation_errors = true;
    }
    else if ( !UITextInput.validateEmail( email ) )
    {
      this.email_input.setError( "FEEDBACK_SCREEN.VALIDATION.EMAIL" );
      has_validation_errors = true;
    }

    if ( message.length === 0 )
    {
      this.message_input.setError( "FEEDBACK_SCREEN.VALIDATION.MESSAGE.EMPTY" );
      has_validation_errors = true;
    }

    if ( has_validation_errors )
      return;

    const send_result = await FeedbackApiController.sendSupportRequest( name, email, message );

    if ( send_result.success )
    {
      this.name_input.setValue( "" );
      this.email_input.setValue( "" );
      this.message_input.setValue( "" );
      this.blurAllInputs();

      this.close();
      FeedbackNotifDialog.open( "FEEDBACK_DIALOG.OK" );
    }
    else
    {
      const error_key = send_result.error_key || "FEEDBACK_DIALOG.ERROR";
      FeedbackNotifDialog.open( error_key );
    }
  }

  public onBackgroundClick(): void
  {
    this.blurAllInputs();
    super.onBackgroundClick();
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "FeedbackDialog";
  }
}
